import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Key, X, Check, Loader2, Info } from "lucide-react";
import Button from "../../../components/common/Button";
import { AdminUserData, PermissionCategory } from "../../../services/userService";
import {
  useRbacMatrixQuery,
  useAssignUserPermissionsMutation,
} from "../../../hooks/queries/useUserAdminQueries";
import { useToast } from "../../../hooks/otherHooks/useToast";

interface UserPermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AdminUserData | null;
  onSuccess: (updatedUser: AdminUserData) => void;
}

const UserPermissionModal: React.FC<UserPermissionModalProps> = ({
  isOpen,
  onClose,
  user,
  onSuccess,
}) => {
  const { addToast } = useToast();
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  // RBAC Catalog Query (shared with SuperAdmin Roles)
  const { data: matrixData, isLoading: isCatalogLoading } = useRbacMatrixQuery();
  const catalog = useMemo<PermissionCategory[]>(
    () => (Array.isArray(matrixData?.catalog) ? matrixData.catalog : []),
    [matrixData?.catalog]
  );
  const loading = isCatalogLoading && !matrixData;

  const { mutateAsync: assignPermissionsMutation, isPending: isSubmitting } =
    useAssignUserPermissionsMutation();

  useEffect(() => {
    if (isOpen && user) {
      setSelectedPermissions(user.user_permissions || []);
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  if (!isOpen || !user) return null;
  if (typeof document === "undefined") return null;

  const handleTogglePermission = (code: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(code) ? prev.filter((p) => p !== code) : [...prev, code]
    );
  };

  const handleToggleCategory = (cat: PermissionCategory) => {
    const catCodes = cat.permissions.map((p) => p.code);
    const allSelected = catCodes.every((c) => selectedPermissions.includes(c));

    if (allSelected) {
      setSelectedPermissions((prev) => prev.filter((c) => !catCodes.includes(c)));
    } else {
      setSelectedPermissions((prev) => Array.from(new Set([...prev, ...catCodes])));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      const res = await assignPermissionsMutation({
        id: user.id,
        payload: {
          permissions: selectedPermissions,
        },
      });

      addToast(res.message || `Permissions updated for ${user.email}`, "success");
      onSuccess(res.user);
      onClose();
    } catch (err: any) {
      console.error("Failed to update user permissions:", err);
      addToast(err?.response?.data?.detail || "Failed to update permissions.", "error");
    }
  };

  const isSuperUser = Boolean(user.is_superuser || user.role === "SUPERADMIN" || (user.roles || []).includes("SUPERADMIN"));

  return createPortal(
    <AnimatePresence>
      <div
        className="cht-fixed cht-inset-0 cht-w-screen cht-h-screen cht-z-[99999] cht-flex cht-items-center cht-justify-center cht-p-4 sm:cht-p-6 cht-bg-slate-950/70 cht-backdrop-blur-sm cht-overflow-y-auto"
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          className="cht-bg-white cht-rounded-[28px] cht-max-w-2xl cht-w-full cht-shadow-2xl cht-border cht-border-gray-100 cht-overflow-hidden cht-flex cht-flex-col cht-max-h-[90vh] cht-my-auto"
        >
          {/* Header */}
          <div className="cht-p-6 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-shrink-0">
            <div className="cht-flex cht-items-center cht-gap-3">
              <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-purple-50 cht-text-purple-600 cht-flex cht-items-center cht-justify-center">
                <Shield size={20} />
              </div>
              <div>
                <h3 className="cht-text-lg cht-font-black cht-text-slate-900 !cht-font-heading">
                  Granular User Permissions
                </h3>
                <p className="cht-text-xs cht-text-slate-500 font-medium">
                  Custom authorization scopes for <span className="cht-text-slate-800 font-bold">{user.name || user.email}</span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="cht-w-8 cht-h-8 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-text-slate-400 hover:cht-bg-slate-100 hover:cht-text-slate-700 cht-transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* SuperUser Notice */}
          {isSuperUser && (
            <div className="cht-px-6 cht-pt-4 cht-shrink-0">
              <div className="cht-p-3.5 cht-rounded-2xl cht-bg-purple-50 cht-border cht-border-purple-200 cht-flex cht-items-center cht-gap-3">
                <Info size={18} className="cht-text-purple-600 cht-shrink-0" />
                <p className="cht-text-xs cht-text-purple-900 leading-relaxed font-medium">
                  This user holds the <strong>SuperAdmin (Root)</strong> role and inherently has unrestricted permissions across all platform services and data models.
                </p>
              </div>
            </div>
          )}

          {/* Permissions Scrollable List */}
          <div className="cht-p-6 cht-overflow-y-auto cht-flex-1 cht-space-y-6">
            {loading ? (
              <div className="cht-py-16 cht-flex cht-flex-col cht-items-center cht-justify-center cht-gap-3">
                <Loader2 size={28} className="cht-animate-spin cht-text-[#3e57da]" />
                <p className="cht-text-xs cht-font-bold cht-text-slate-500">Loading permission matrix...</p>
              </div>
            ) : catalog.length === 0 ? (
              <p className="cht-text-xs cht-text-slate-400 cht-text-center cht-py-8">No permission scopes found.</p>
            ) : (
              catalog.map((cat) => {
                const catCodes = cat.permissions.map((p) => p.code);
                const allSelected = catCodes.every((c) => selectedPermissions.includes(c));
                const someSelected = catCodes.some((c) => selectedPermissions.includes(c));

                return (
                  <div key={cat.category} className="cht-space-y-3">
                    <div className="cht-flex cht-items-center cht-justify-between cht-border-b cht-border-slate-100 cht-pb-2">
                      <div>
                        <h4 className="cht-text-xs cht-font-black cht-text-slate-900 cht-uppercase cht-tracking-wider">
                          {cat.category}
                        </h4>
                        <p className="cht-text-[11px] cht-text-slate-400">{cat.description}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleToggleCategory(cat)}
                        className="cht-text-[11px] cht-font-bold cht-text-[#3e57da] hover:cht-underline"
                      >
                        {allSelected ? "Deselect All" : "Select All"}
                      </button>
                    </div>

                    <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-2.5">
                      {cat.permissions.map((perm) => {
                        const isChecked = selectedPermissions.includes(perm.code);

                        return (
                          <div
                            key={perm.code}
                            onClick={() => handleTogglePermission(perm.code)}
                            className={`cht-p-3 cht-rounded-xl cht-border cht-cursor-pointer cht-flex cht-items-start cht-gap-2.5 cht-transition-all ${
                              isChecked
                                ? "cht-border-purple-200 cht-bg-purple-50/50"
                                : "cht-border-slate-200 cht-bg-white hover:cht-border-slate-300"
                            }`}
                          >
                            <div className={`cht-mt-0.5 cht-w-4 cht-h-4 cht-rounded-md cht-border cht-flex cht-items-center cht-justify-center cht-shrink-0 cht-transition-colors ${
                              isChecked ? "cht-border-purple-600 cht-bg-purple-600 cht-text-white" : "cht-border-slate-300 cht-bg-white"
                            }`}>
                              {isChecked && <Check size={11} strokeWidth={3} />}
                            </div>
                            <div className="cht-min-w-0">
                              <p className="cht-text-xs cht-font-bold cht-text-slate-900 cht-truncate">
                                {perm.name}
                              </p>
                              <p className="cht-text-[10px] cht-text-slate-400 leading-snug">
                                {perm.description}
                              </p>
                              <span className="cht-inline-block cht-mt-1 cht-text-[9px] cht-font-mono cht-text-slate-400">
                                {perm.code}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="cht-p-6 cht-border-t cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-shrink-0">
            <span className="cht-text-xs cht-font-bold cht-text-slate-500">
              {selectedPermissions.length} permission{selectedPermissions.length === 1 ? "" : "s"} assigned
            </span>
            <div className="cht-flex cht-items-center cht-gap-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="cht-rounded-xl"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="cht-rounded-xl cht-px-6"
                onClick={handleSubmit}
                isLoading={isSubmitting}
              >
                Save Permissions
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default UserPermissionModal;
